import { Components as JoyComponents, Theme as JoyTheme } from '@mui/joy';
import type { CheckboxProps as JoyCheckboxProps } from '@mui/joy/Checkbox';
import JoyCheckbox from '@mui/joy/Checkbox';
import { mergeProps, mergeRefs, useObjectRef } from '@react-aria/utils';
import clsx from 'clsx';
import { forwardRef, useState } from 'react';
import { FieldPath, FieldValues, useController } from 'react-hook-form';

import { Description, Label } from './fieldset';
import {
  ComposedFormControllerProps,
  FormController,
  useFormController,
} from './form';
import { createSplitProps } from './utils';

export const overrides: JoyComponents<JoyTheme>['JoyCheckbox'] = {
  styleOverrides: {
    checkbox: ({ ownerState, theme }) => ({
      ...(ownerState.disabled && {
        backgroundColor: theme.palette.neutral[50],
      }),
    }),
  },
};

export function CheckboxGroup({
  className,
  ...props
}: Readonly<React.ComponentPropsWithoutRef<'div'>>) {
  return (
    <div
      data-slot='control'
      {...props}
      className={clsx(
        className,

        // Basic groups
        'space-y-3',

        // With descriptions
        'has-[[data-slot=description]]:space-y-6 [&_[data-slot=label]]:has-[[data-slot=description]]:font-medium',
      )}
    />
  );
}

export function CheckboxGroupField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  label,
  description,
  options,
  ...props
}: ComposedFormControllerProps<T, TName> &
  Omit<
    React.ComponentProps<typeof Checkbox>,
    keyof ComposedFormControllerProps<T, TName>
  > & {
    label?: React.ReactNode;
    description?: React.ReactNode;
    options: string[];
  }) {
  const [controllerProps] = createSplitProps<
    ComposedFormControllerProps<T, TName>
  >()(props, [
    'color',
    'color',
    'defaultValue',
    'disabled',
    'field',
    'control',
    'size',
    'required',
  ]);

  const { field: fieldName, control } = controllerProps;

  const { field } = useController({
    control,
    name: fieldName,
  });

  const [value, setValue] = useState<string[]>(field.value ?? []);
  return (
    <CheckboxGroup>
      {options.map((option, index) => (
        <FormController {...controllerProps} key={`${option}-${index}`}>
          <div className={clsx(checkboxLayoutStyles)}>
            <JoyCheckbox
              onChange={(e) => {
                const valueCopy = [...value];
                valueCopy[index] = e.target.checked ? e.target.value : '';
                field.onChange(valueCopy.filter((valueOption) => valueOption));
                setValue(valueCopy);
              }}
              key={option}
              checked={value.includes(option)}
              value={option}
            />
            <Label>{option}</Label>
          </div>
        </FormController>
      ))}
    </CheckboxGroup>
  );
}

export function CheckboxGroupFieldV2<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  label,
  description,
  options,
  ...props
}: ComposedFormControllerProps<T, TName> &
  Omit<
    React.ComponentProps<typeof Checkbox>,
    keyof ComposedFormControllerProps<T, TName>
  > & {
    label?: React.ReactNode;
    description?: React.ReactNode;
    options: { id: string; label: string }[];
  }) {
  const [controllerProps] = createSplitProps<
    ComposedFormControllerProps<T, TName>
  >()(props, [
    'color',
    'color',
    'defaultValue',
    'disabled',
    'field',
    'control',
    'size',
    'required',
  ]);

  const { field: fieldName, control } = controllerProps;

  const { field } = useController({
    control,
    name: fieldName,
  });

  const [value, setValue] = useState<string[]>(field.value ?? []);
  return (
    <CheckboxGroup>
      {options.map((option, index) => (
        <FormController {...controllerProps} key={`${option.id}-${index}`}>
          <div className={clsx(checkboxLayoutStyles)}>
            <JoyCheckbox
              onChange={(e) => {
                const valueCopy = [...value];
                valueCopy[index] = e.target.checked ? e.target.value : '';
                field.onChange(valueCopy.filter((valueOption) => valueOption));
                setValue(valueCopy);
              }}
              key={option.id}
              checked={value.includes(option.label)}
              value={option.label}
            />
            <Label>{option.label}</Label>
          </div>
        </FormController>
      ))}
    </CheckboxGroup>
  );
}

export const Checkbox = forwardRef<
  HTMLDivElement,
  JoyCheckboxProps & { controlled?: boolean }
>(function Checkbox({ controlled = true, ...props }, ref) {
  const controller = useFormController();
  const objectRef = useObjectRef(ref);
  const mergedRef = mergeRefs(ref, objectRef);
  return (
    <JoyCheckbox
      {...mergeProps(
        props,
        controller && controlled
          ? { ...controller?.field, checked: controller.field.value }
          : { ref: mergedRef },
      )}
    />
  );
});

export const checkboxLayoutStyles = [
  // Base layout
  'grid grid-cols-[1.125rem_1fr] items-center gap-x-4 gap-y-1 sm:grid-cols-[1rem_1fr]',

  // Control layout
  '[&>[data-slot=control]]:col-start-1 [&>[data-slot=control]]:row-start-1 [&>[data-slot=control]]:justify-self-center',

  // Label layout
  '[&>[data-slot=label]]:col-start-2 [&>[data-slot=label]]:row-start-1 [&>[data-slot=label]]:justify-self-start',

  // Description layout
  '[&>[data-slot=description]]:col-start-2 [&>[data-slot=description]]:row-start-2',

  // With description
  '[&_[data-slot=label]]:has-[[data-slot=description]]:font-medium',
];

export function CheckboxField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  label,
  description,
  ...props
}: ComposedFormControllerProps<T, TName> &
  Omit<
    React.ComponentProps<typeof Checkbox>,
    keyof ComposedFormControllerProps<T, TName>
  > & {
    label?: React.ReactNode;
    description?: React.ReactNode;
  }) {
  const [controllerProps, inputProps] = createSplitProps<
    ComposedFormControllerProps<T, TName>
  >()(props, [
    'color',
    'color',
    'defaultValue',
    'disabled',
    'field',
    'control',
    'size',
    'required',
  ]);

  return (
    <FormController {...controllerProps}>
      <div className={clsx(checkboxLayoutStyles)}>
        <Checkbox {...inputProps} />
        {label && <Label>{label}</Label>}
        {description && <Description>{description}</Description>}
      </div>
    </FormController>
  );
}
